import { useEffect, useState } from 'react';
import Docs from './docs';
import RefiDocs from './refiDocs';
import CustomBreadcrumbs from '../../components/common/CustomBreadcrumbs';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CircularIndeterminate from '../../components/common/Progress';
import { SUBMIT_DOC, SUBMIT_REFIDOC } from '../../redux-state/docs/types';
import { CLEAR_FORM_INPUTS } from '../../redux-state/forms/types';
import { selectDocsLoadingState } from '../../redux-state/docs/selectors';
import { selectLocationInfo, selectTransactionInfo, selectVehicleInfo } from '../../redux-state/forms/selectors';
import FormValidation from '../../components/utility/formValidation';
import scrollToFirstError from '../../components/utility/scrollToFirstError';
import PrimaryButton from '../../components/common/button/PrimaryButton';
import SecondaryButton from '../../components/common/button/SecondaryButton';
import ClearFormDialog from '../../components/common/clearFormDialog';

function DocumentWrapper({ formErrors }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const loadingState = useSelector(selectDocsLoadingState) || false;
  const locationInfo = useSelector(selectLocationInfo);
  const vehicleInfo = useSelector(selectVehicleInfo);
  const transactionInfo = useSelector(selectTransactionInfo);
  const [errors, setErrors] = useState({});
  const [openClearConfirm, setOpenClearConfirm] = useState(false);
  const transactionType = transactionInfo.transactionType
  const isRefiDocs = transactionType === 'Refinance';

  useEffect(() => {
    if (formErrors) {
      setErrors(formErrors);
    }
  }, [formErrors]);

  const handleSubmit = () => {
    const formData = { ...locationInfo, ...vehicleInfo, ...transactionInfo };

    const validationResult = FormValidation('Docs', formData);

    if (validationResult) {
      setErrors(validationResult);
      scrollToFirstError();
      return;
    }

    setErrors({});

    dispatch({
      type: isRefiDocs ? SUBMIT_REFIDOC: SUBMIT_DOC,
      navigate: navigate,
      clearFormsOnSuccess: true,
    });
  }

  const handleBack = () => {
    navigate('/dashboard');
  };

  // CLEAR_FORM_INPUTS keeps transactionInfo.transactionType, which is what
  // isRefiDocs above is derived from -- so clearing the Refi-Docs form leaves
  // the user on the Refi-Docs form instead of silently swapping them onto the
  // purchase one. See redux-state/forms/reducer.js.
  const handleClearForm = () => {
    dispatch({ type: CLEAR_FORM_INPUTS });
    setErrors({});
    setOpenClearConfirm(false);
  };

  if (location.pathname.includes('docsOutput') || location.pathname.includes('refiDocsOutput')) {
    return <Outlet />;
  }
  const isMultiStep = location.state?.fromMultiStep;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="p-6 flex-1">
        <CustomBreadcrumbs title={isRefiDocs ? 'Refi-Docs' : 'Docs'} />
        <h1 className="text-3xl font-bold text-gray-900 mt-4">
          {isRefiDocs ? 'Refi-Docs' : 'Docs'}
        </h1>
        <p className="mt-2 text-gray-600">
          Please provide any details you would like pre-populated in the document package.
        </p>

        {isRefiDocs ? (
          <RefiDocs
            errors={errors}
            isMultiStep={isMultiStep}
          />
        ) : (
          <Docs
            errors={errors}
            isMultiStep={isMultiStep}
          />
        )}
      </div>

      {loadingState && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <CircularIndeterminate />
        </div>
      )}

      { !isMultiStep &&
        <div className="flex justify-between bg-white p-6 border-t border-gray-200">
          <SecondaryButton
            text="Back to Home"
            onClick={handleBack}
          />

          <SecondaryButton
            text="Clear Form"
            onClick={() => setOpenClearConfirm(true)}
            disabled={loadingState}
          />

          <PrimaryButton
            text={isRefiDocs ? 'Submit' : 'Submit'}
            onClick={handleSubmit}
            disabled={loadingState}
          />
        </div>
       }

      <ClearFormDialog
        open={openClearConfirm}
        onConfirm={handleClearForm}
        onClose={() => setOpenClearConfirm(false)}
      />
    </div>
  );
}

export default DocumentWrapper;